import { useEffect } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router";
import { AppShell } from "./AppShell";
import { AppError } from "./AppError";
import { isStale } from "@/lib/version";
import { useMe } from "@/features/auth/hooks";
import { can, type Me, type Permission, type Role } from "@/features/auth/types";
import { PageLoading } from "@/components/ui/misc";
import { onPasswordChangeRequired, onTwofaSetupRequired, onUnauthorized } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { LoginPage } from "@/pages/auth/LoginPage";
import { TwoFactorPage } from "@/pages/auth/TwoFactorPage";
import { TwoFactorSetupPage } from "@/pages/auth/TwoFactorSetupPage";
import { SetPasswordPage } from "@/pages/auth/SetPasswordPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { ProfilePage } from "@/pages/profile/ProfilePage";
import { PersonPage } from "@/pages/people/PersonPage";
import { PipelinePage } from "@/pages/pipeline";
import { DriversPage, DriverProfilePage, NewDriverPage } from "@/pages/drivers";
import { ApprovalsPage } from "@/pages/approvals";
import { NotificationsPage } from "@/pages/notifications";
import { TasksPage } from "@/pages/tasks";
import { CarriersPage, CarrierProfilePage } from "@/pages/carriers";
import { JobsPage, JobDetailPage } from "@/pages/jobs";
import { HomePage } from "@/pages/home";
import { PeoplePage } from "@/pages/people/PeoplePage";
import { ReportsPage } from "@/pages/reports";
import { UsersPage, BranchesPage, SettingsPage, AuditLogPage, ImportPage } from "@/pages/admin";
import { BudgetPage, ExpensesPage, SalariesPage, BonusesPage, PlacementsPage, MoneyPage } from "@/pages/budget";
import { ApplyPage, CarrierPortalPage } from "@/pages/public";

onUnauthorized(() => {
  queryClient.setQueryData(["auth", "me"], null);
});
// Backend platformani 2FA sozlanmaguncha yopdi: "me" qayta o'qiladi va sozlash sahifasi chiqadi
onTwofaSetupRequired(() => {
  queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
});
// Owner/menejer yangi (vaqtinchalik) parol berdi: "me" qayta o'qiladi va parol qo'yish sahifasi chiqadi
onPasswordChangeRequired(() => {
  queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
});

/** Faqat kirgan foydalanuvchi; bo'lmasa /login */
function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  // Yangi deploy chiqqan bo'lsa (lib/version.ts) keyingi sahifa almashishida tab yangilanadi: yozilayotgan forma yo'qolmaydi
  useEffect(() => {
    if (isStale()) window.location.reload();
  }, [location.pathname]);
  if (me.isPending) return <PageLoading />;
  if (!me.data) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.data.role === "carrier") return <Navigate to="/portal" replace />;
  // Vaqtinchalik parol bilan kirdi (owner/menejer bergan): avval o'z parolini qo'yadi (backend ham yopiq tutadi)
  if (me.data.must_change_password) return <SetPasswordPage />;
  // Roli uchun 2FA majburiy, lekin hali sozlanmagan: platforma o'rniga sozlash (backend ham yopiq tutadi)
  if (me.data.must_setup_2fa) return <TwoFactorSetupPage />;
  return <AppShell me={me.data} />;
}

function RequireRole({ allow }: { allow: (role: Role) => boolean }) {
  const me = useMe();
  if (!me.data) return null;
  if (!allow(me.data.role)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Bo'lim ruxsati (kamida bittasi). Owner ruxsatni olib qo'ysa, ochiq sahifa ham darhol yopiladi (me yangilanadi). */
function RequirePerm({ perm }: { perm: Permission | Permission[] }) {
  const me = useMe();
  if (!me.data) return null;
  const perms = Array.isArray(perm) ? perm : [perm];
  if (!perms.some((p) => can(me.data, p))) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Budjet bosh sahifasi: umumiy ko'rinish; faqat "pay" ruxsati bo'lsa -- Maoshlar */
function BudgetHome() {
  const me = useMe();
  if (can(me.data, "budget")) return <BudgetPage />;
  return <Navigate to="/budget/salaries" replace />;
}

/** Login sahifalari: allaqachon kirgan bo'lsa bosh sahifaga */
function PublicOnly() {
  const me = useMe();
  if (me.isPending) return <PageLoading />;
  if (me.data) return <Navigate to={me.data.role === "carrier" ? "/portal" : "/"} replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <PublicOnly />,
    errorElement: <AppError />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/login/2fa", element: <TwoFactorPage /> },
    ],
  },
  { path: "/apply", element: <ApplyPage />, errorElement: <AppError /> },
  { path: "/portal/*", element: <CarrierPortalPage />, errorElement: <AppError /> },
  {
    element: <RequireAuth />,
    errorElement: <AppError />,
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/tasks", element: <TasksPage /> },
      { path: "/profile", element: <ProfilePage /> },
      { path: "/people", element: <PeoplePage /> },
      { path: "/people/:id", element: <PersonPage /> },
      { path: "/notifications", element: <NotificationsPage /> },
      { path: "/drivers", element: <DriversPage /> },
      { path: "/drivers/new", element: <NewDriverPage /> },
      { path: "/drivers/:id", element: <DriverProfilePage /> },
      { path: "/pipeline", element: <PipelinePage /> },
      { path: "/carriers", element: <CarriersPage /> },
      { path: "/carriers/:id", element: <CarrierProfilePage /> },
      { path: "/jobs", element: <JobsPage /> },
      { path: "/jobs/:id", element: <JobDetailPage /> },
      // Bo'limlar ruxsat bo'yicha (features/auth/types.ts -> Permission); backend xuddi shuni tekshiradi
      { element: <RequirePerm perm="reports" />, children: [{ path: "/reports", element: <ReportsPage /> }] },
      { element: <RequirePerm perm="approvals" />, children: [{ path: "/approvals", element: <ApprovalsPage /> }] },
      { element: <RequirePerm perm={["budget", "pay"]} />, children: [{ path: "/budget", element: <BudgetHome /> }] },
      {
        element: <RequirePerm perm="budget" />,
        children: [
          { path: "/budget/money", element: <MoneyPage /> },
          { path: "/budget/expenses", element: <ExpensesPage /> },
          { path: "/budget/placements", element: <PlacementsPage /> },
        ],
      },
      {
        element: <RequirePerm perm="pay" />,
        children: [
          { path: "/budget/salaries", element: <SalariesPage /> },
          { path: "/budget/bonuses", element: <BonusesPage /> },
        ],
      },
      {
        element: <RequirePerm perm="users" />,
        children: [
          { path: "/admin", element: <Navigate to="/admin/users" replace /> },
          { path: "/admin/users", element: <UsersPage /> },
        ],
      },
      {
        element: <RequireRole allow={(role) => role === "owner"} />,
        children: [
          { path: "/admin/branches", element: <BranchesPage /> },
          { path: "/admin/settings", element: <SettingsPage /> },
          { path: "/admin/import", element: <ImportPage /> },
          { path: "/admin/audit", element: <AuditLogPage /> },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export type { Me };
